import type { AxiosError } from 'axios';

export function axiosRequestPath(config: { baseURL?: string, url?: string } | undefined): string {
  if (!config) return 'unknown';
  const raw = `${config.baseURL ?? ''}${config.url ?? ''}`.split('?')[0];
  if (!raw) return 'unknown';
  try {
    if (/^https?:\/\//i.test(raw)) return new URL(raw).pathname || '/';
  } catch {
    /* fall through */
  }
  return raw.startsWith('/') ? raw : `/${raw}`;
}

export function getAxiosEffectiveHttpStatus(error: AxiosError): number | undefined {
  const statusCode = error.response?.status;
  if (typeof statusCode !== 'number' || statusCode < 100 || statusCode > 599) return undefined;
  return statusCode;
}

export function normalizePathForDisplay(path: string): string {
  return path
    .replace(/\b[a-f0-9]{24}\b/gi, ':id')
    .replace(/\b[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}\b/gi, ':uuid')
    .replace(/\/\d{6,}(?=\/|$)/g, '/:id');
}

type NoHttpStatusClassification = { fingerprintKey: string, description: string };

function classifyAxiosErrorWhenNoValidHttpStatus(error: AxiosError): NoHttpStatusClassification {
  if (error.code === 'ERR_NETWORK') {
    return { fingerprintKey: 'network', description: 'NETWORK' };
  }
  if (error.code === 'ECONNABORTED' || error.message?.toLowerCase().includes('timeout')) {
    return { fingerprintKey: 'timeout', description: 'TIMEOUT' };
  }
  if (error.response?.status === 0) {
    return {
      fingerprintKey: 'status-0-no-http',
      description: 'NO HTTP STATUS (often CORS, blocked request, or network)'
    };
  }
  return { fingerprintKey: 'no-response', description: error.code ?? 'NO RESPONSE' };
}

export function getAxiosFingerprintStatusKey(
  error: AxiosError,
  httpStatus: number | undefined
): string {
  if (httpStatus != null) return String(httpStatus);
  return classifyAxiosErrorWhenNoValidHttpStatus(error).fingerprintKey;
}

export function describeAxiosFailureWithoutHttpStatus(error: AxiosError): string {
  return classifyAxiosErrorWhenNoValidHttpStatus(error).description;
}
